import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { StickyMobileCTA } from "@/components/site/StickyMobileCTA";
import { Toaster } from "@/components/ui/sonner";
import { site } from "@/lib/site";

const AssistantChat = lazy(() =>
  import("@/components/site/AssistantChat").then((m) => ({ default: m.AssistantChat })),
);
const ExitIntentPopup = lazy(() =>
  import("@/components/site/ExitIntentPopup").then((m) => ({ default: m.ExitIntentPopup })),
);

const legalServiceJsonLd = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: "Nkadimeng Danny Legal & Compliance Support",
  legalName: site.name,
  description:
    "Legal information and legal advice on non-reserved matters, legal opinions, paralegal support and compliance services across South Africa — labour relations, CCMA assistance, contract drafting, debt support and tutoring. Not an admitted attorney firm: no reserved work or court representation.",
  knowsAbout: [
    "Legal advice on non-reserved matters",
    "Legal opinions",
    "Labour law and CCMA support",
    "POPIA, BCEA and B-BBEE compliance",
    "Contract drafting",
  ],
  disambiguatingDescription:
    "We do provide legal advice, but only on non-reserved matters. We do not undertake work reserved by the Legal Practice Act 28 of 2014 and we do not represent clients in court.",

  url: "https://nkadimengdannylcs.com",
  telephone: site.phoneIntl,
  email: site.email,
  taxID: site.registration.taxNumber,
  identifier: {
    "@type": "PropertyValue",
    propertyID: "CIPC Registration",
    value: site.registration.number,
  },
  foundingDate: "2026-07-01",
  address: {
    "@type": "PostalAddress",
    streetAddress: site.address.line1,
    addressLocality: "Dennilton",
    addressRegion: site.address.province,
    postalCode: site.address.postalCode,
    addressCountry: "ZA",
  },
  areaServed: { "@type": "Country", name: "South Africa" },
  priceRange: "ZAR",
  availableLanguage: ["English", "Sepedi", "Zulu", "Xhosa", "Afrikaans"],
};

function NotFoundComponent() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">404</p>
        <h1 className="mt-3 font-display text-4xl text-navy">Page not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-md bg-navy px-5 py-2.5 text-sm font-medium text-navy-foreground"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl text-navy">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. Please try again or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Nkadimeng Danny Legal & Compliance Support" },
      { name: "google-site-verification", content: "4iLzKrmj1q1wqwMtF4no-oYHmqSvzc7pIamTiLE--6k" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Nkadimeng Danny Legal & Compliance Support" },
      { property: "og:image", content: "https://nkadimengdannylcs.com/__l5e/assets-v1/e095051d-6242-4d2a-a72e-391f32b57343/danny-main-logo.jpg" },
      { property: "og:image:alt", content: "Nkadimeng Danny Legal & Compliance Support logo" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://nkadimengdannylcs.com/__l5e/assets-v1/e095051d-6242-4d2a-a72e-391f32b57343/danny-main-logo.jpg" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Client-side purge for any residual "Edit with Lovable" badge / gpteng
// script injection. The badge is disabled in publish settings, but we
// defensively strip any DOM/script node that matches known selectors and
// keep watching for future injections so it can't reappear.
const BADGE_PURGE_SCRIPT = `(() => {
  if (typeof document === 'undefined') return;
  var SEL = [
    '[data-lovable-badge]','#lovable-badge','.lovable-badge',
    'a[href*="lovable.dev"]','a[href*="lovable.app/projects"]',
    'iframe[src*="lovable.dev"]','iframe[src*="gpteng.co"]',
    'script[src*="gpteng.co"]','script[src*="cdn.gpteng.co"]',
    'link[href*="gpteng.co"]'
  ].join(',');
  function purge(root){ try { root.querySelectorAll(SEL).forEach(function(n){ n.remove(); }); } catch(e){} }
  purge(document);
  try {
    new MutationObserver(function(muts){
      for (var i=0;i<muts.length;i++){
        var m = muts[i];
        for (var j=0;j<m.addedNodes.length;j++){
          var n = m.addedNodes[j];
          if (n && n.nodeType === 1){
            if (n.matches && n.matches(SEL)) { n.remove(); continue; }
            purge(n);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  } catch(e){}
})();`;

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <script dangerouslySetInnerHTML={{ __html: BADGE_PURGE_SCRIPT }} />
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const [deferredReady, setDeferredReady] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isLxp = pathname === "/lexpractiq" || pathname.startsWith("/lexpractiq/");
  useEffect(() => {
    const win = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const idle = win.requestIdleCallback
      ? win.requestIdleCallback(() => setDeferredReady(true), { timeout: 2500 })
      : (window.setTimeout(() => setDeferredReady(true), 2000) as unknown as number);
    return () => {
      if (win.requestIdleCallback) {
        (window as any).cancelIdleCallback?.(idle);
      } else {
        window.clearTimeout(idle as number);
      }
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col pb-14 md:pb-0">
        {!isLxp && <Header />}
        <main className="flex-1">
          <Outlet />
        </main>
        {!isLxp && <Footer />}
        {!isLxp && <StickyMobileCTA />}
        {deferredReady && (
          <Suspense fallback={null}>
            <AssistantChat />
            <ExitIntentPopup />
          </Suspense>
        )}
        <Toaster richColors position="top-center" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(legalServiceJsonLd) }}
        />
      </div>
    </QueryClientProvider>
  );
}
